import { Alert, AlertDescription, Button, Input, Label } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';


import { adminLogin, type AdminTotpSetupResponse } from '../../../api/auth.js';
import { ApiError } from '../../../lib/api-client.js';
import { AuthShell } from '../../auth/auth-shell.js';

interface PendingTotpSetup extends AdminTotpSetupResponse {
  email: string;
  password: string;
}

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);

  const mutation = useMutation({
    mutationFn: () => adminLogin({ email, password, totpCode: totpCode || undefined }),
    onSuccess: (response) => {
      if ('totpSetupRequired' in response) {
        const pending: PendingTotpSetup = { ...response, email, password };
        sessionStorage.setItem('sitewise:totp-setup', JSON.stringify(pending));
        navigate('/admin/totp-setup', { replace: true });
        return;
      }
      navigate('/admin/dashboard', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
      }
    },
  });

  return (
    <AuthShell
      title="Super-admin sign-in"
      description="The platform console requires email, password, and TOTP."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="admin-email">Email</Label>
          <Input
            id="admin-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="admin-password">Password</Label>
          <Input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {needsTotp ? (
          <div className="space-y-1.5">
            <Label htmlFor="admin-totp">6-digit code</Label>
            <Input
              id="admin-totp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              required
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D+/g, ''))}
              autoFocus
            />
          </div>
        ) : null}

        {mutation.error &&
        !(mutation.error instanceof ApiError && mutation.error.message === 'TOTP_REQUIRED') ? (
          <Alert variant="danger">
            <AlertDescription>
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : 'Sign-in failed.'}
            </AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? 'Signing in…' : needsTotp ? 'Verify code' : 'Continue'}
        </Button>
      </form>
    </AuthShell>
  );
}
